import { getSessionAuthentication, baseUrl } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
    locale?: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface PrintDocumentTemplate {
    id: number;
    description: string;
}

export interface InvoiceType {
    id: number;
    name: string;
    printDocumentTemplate: PrintDocumentTemplate | null;
    localizations: Localization[];
}

export async function getInvoiceType(id: number): Promise<InvoiceType> {
    return legacyFetch<InvoiceType>(`invoicetypes/${id}`);
}

export async function createInvoiceType(it: Partial<InvoiceType>): Promise<InvoiceType> {
    return legacyFetch<InvoiceType>('invoicetypes', {
        method: 'POST',
        body: JSON.stringify(it),
    });
}

export async function updateInvoiceType(it: InvoiceType): Promise<InvoiceType> {
    return legacyFetch<InvoiceType>(`invoicetypes/${it.id}`, {
        method: 'PUT',
        body: JSON.stringify(it),
    });
}

export async function deleteInvoiceType(id: number): Promise<void> {
    await legacyFetch<void>(`invoicetypes/${id}`, { method: 'DELETE' });
}

export async function getLanguages(): Promise<Language[]> {
    return legacyFetch<Language[]>('invoicetypes/languages');
}

export async function getPrintDocumentTemplates(): Promise<PrintDocumentTemplate[]> {
    return legacyFetch<PrintDocumentTemplate[]>('invoicetypes/printDocumentTemplates');
}
